import { useState } from "react";
import { useLocation, Link } from "wouter";
import { signUp, getSession } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	CardFooter,
} from "@/components/ui/card";
import { Bot, Loader2, UserPlus, ArrowLeft } from "lucide-react";

export function RegisterPage() {
	const [, setLocation] = useLocation();
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);

		try {
			const result = await signUp.email({
				email,
				password,
				name,
			});

			if (result.error) {
				setError(result.error.message || "Could not create account");
			} else {
				// Wait for session to be established before redirecting
				await new Promise(resolve => setTimeout(resolve, 100));
				const session = await getSession();
				if (session?.data?.session) {
					setLocation("/");
				} else {
					// Fallback: force a page reload to ensure session is loaded
					window.location.href = "/";
				}
			}
		} catch (err) {
			setError("An error occurred. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/10 p-4">
			{/* Background decoration */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute -top-40 -left-20 w-80 h-80 bg-primary/10 rounded-full blur-3xl opacity-50" />
				<div className="absolute top-1/2 -right-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50" />
				<div className="absolute -bottom-40 left-1/2 w-80 h-80 bg-primary/20 rounded-full blur-3xl opacity-30" />
			</div>

			<Card className="w-full max-w-md relative z-10 shadow-2xl border-border/50 backdrop-blur-md bg-card/80">
				<CardHeader className="text-center pb-2">
					<div className="mx-auto w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-4 shadow-lg animate-in zoom-in duration-500">
						<UserPlus className="w-9 h-9 text-primary-foreground" />
					</div>
					<CardTitle className="text-2xl font-bold tracking-tight">
						Create Account
					</CardTitle>
					<CardDescription className="text-base text-muted-foreground">
						Join the WhatsApp AI Agent Dashboard
					</CardDescription>
				</CardHeader>
				<CardContent className="pt-4 px-8">
					<form onSubmit={handleSubmit} className="space-y-5">
						<div className="space-y-2">
							<Label htmlFor="name">Full Name</Label>
							<Input
								id="name"
								type="text"
								placeholder="John Doe"
								value={name}
								onChange={(e) => setName(e.target.value)}
								required
								disabled={isLoading}
								className="h-11 bg-background/50 border-border/60 focus:ring-2 focus:ring-primary/20 transition-all"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="email">Email Address</Label>
							<Input
								id="email"
								type="email"
								placeholder="you@example.com"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								disabled={isLoading}
								className="h-11 bg-background/50 border-border/60 focus:ring-2 focus:ring-primary/20 transition-all"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								type="password"
								placeholder="••••••••"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								disabled={isLoading}
								className="h-11 bg-background/50 border-border/60 focus:ring-2 focus:ring-primary/20 transition-all"
							/>
						</div>

						{error && (
							<div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/20 animate-in fade-in slide-in-from-top-1">
								{error}
							</div>
						)}

						<Button
							type="submit"
							className="w-full h-12 text-base font-semibold shadow-md active:scale-[0.98] transition-all"
							disabled={isLoading}
						>
							{isLoading ? (
								<>
									<Loader2 className="w-5 h-5 mr-3 animate-spin" />
									Creating account...
								</>
							) : (
								"Get Started"
							)}
						</Button>
					</form>
				</CardContent>
				<CardFooter className="flex flex-col space-y-4 pb-8">
					<div className="text-sm text-center text-muted-foreground">
						Already have an account?{" "}
						<Link
							href="/login"
							className="text-primary font-semibold hover:underline transition-all"
						>
							Log in here
						</Link>
					</div>
					<Link
						href="/login"
						className="text-xs flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
					>
						<ArrowLeft className="w-3 h-3 mr-1" /> Back to Login
					</Link>
				</CardFooter>
			</Card>
		</div>
	);
}
